import React, { useCallback, useEffect, useRef, useState } from 'react';
import Game2dMenu from '../TwoD/Controls/Game2dMenu';
import GamePlayer2dMenu from '../TwoD/Controls/GamePlayer2dMenu';
import GamePlayer2d from '../TwoD/GamePlayer2d';
import HotelInterface
    from '../TwoD/Components/Controls/HotelInterface';
import BoardActionRouter
    from '../TwoD/Components/Controls/Actions/BoardActionRouter';
import { AdvertisementPossibilities }
    from '../TwoD/Components/Controls/Actions/AdvertisementController';
import Game2d from '../TwoD/Game2d';
import TranscriptTicker
    from '../TwoD/Components/Controls/TranscriptTicker';
import MessageTicker from '../TwoD/Components/Views/MessageTicker';
import ActivityFeed
    from '../TwoD/Components/Controls/ActivityFeed';
import EventRouter
    from '../TwoD/Components/Views/Events/EventRouter';
import { AutomaticPlayerControls }
    from '../TwoD/Components/Controls/AutomaticPlayerControls';
import TheRiver from '../TwoD/Components/Controls/TheRiver';
import GamePlayerPools2d from '../TwoD/Controls/Pools';
import { DeckGrid } from '../TwoD/Components/Views/DeckBar';

import {
    collapseEmptyAreaFromPreset,
    DESKTOP,
    PRESETS, PRESET_DISPLAY_NAMES, PRESET_NAMES,
} from './presets';
import { useDashboardPreset } from './useDashboardPreset';
import { useOnDemandPanel } from './useOnDemandPanel';
import { FloatingWindow } from './FloatingWindow';
import CompetitorsWindow from './CompetitorsWindow';
import InsightsWindow from '../Insights/InsightsWindow';
import ScoresWindow from './ScoresWindow';
import { useFloatingWindows } from './useFloatingWindows';
import ActionWindow
    from './ActionWindows/ActionWindow';
import LodgingActionWindow
    from './ActionWindows/LodgingActionWindow';
import ResearchActionWindow
    from './ActionWindows/ResearchActionWindow';
import GhostActionWindow
    from './ActionWindows/GhostActionWindow';
import LobbyOverlay from '../Menus/Lobby/LobbyOverlay';
import ChooseFaction from '../TwoD/Choices/ChooseFaction';
import OpeningGhostPlacement
    from '../TwoD/Choices/OpeningGhostPlacement';
import DraftOverlay from '../TwoD/Choices/draft/DraftOverlay';

import './Dashboard.css';

// Manual 3.1 opening ghost placement (engine: see
// games/models.GamePlayerStatus.OPENING_GHOST_PLACEMENT_ID). Async —
// every seat is in this status simultaneously, no current_player gate.
const OPENING_GHOST_PLACEMENT_ID = 32;
// Manual §3 in-game faction ceremony (engine: see
// games/models.GamePlayerStatus.CHOOSE_FACTION_ID). Clockwise from
// Player One — all seats sit in this status while the ceremony is
// open, only current_player picks at any moment. Mirrors the
// InitialLodgingChoice gating pattern.
const CHOOSE_FACTION_ID = 33;

// CC_LOBBY_OVERLAY_WINDOW (2026-06-07): the in-game Lobby overlay window.
// Summonable (open/close) from the in-game menu (EscapeMenu fires
// TOGGLE_LOBBY_WINDOW_EVENT). It is NOT a useFloatingWindows panel — those
// are always-on and persisted; this one toggles, so DesktopRender owns its
// open flag + geometry locally. Its z sits above every persistent panel
// (snapshot max ~290) so it floats on top when summoned. COPY PLACEHOLDER
// title — Michael owns the wording.
const TOGGLE_LOBBY_WINDOW_EVENT = 'toggle-lobby-window-event';
const LOBBY_WINDOW_NAME = 'lobbyOverlay';
const LOBBY_WINDOW_TITLE = 'Lobby';
const LOBBY_WINDOW_DEFAULT = {
    x: 480, y: 120, width: 520, height: 600, z: 1000,
};

// CeremonyOverlays hoists the two status-gated ceremony pickers out of
// GamePlayer2d so a single mount point serves both the Desktop
// floating-window render path and the preset (Mobile / Phone
// Companion / etc.) grid render path. Both overlays portal to
// document.body, so they have no layout-chain dependency on the
// surrounding tree — mounting them here is purely a mount-site move.
function CeremonyOverlays(props) {
    const seated = !!props.gamePlayerData;
    if (!seated) return null;
    const status = props.gamePlayerData.status;
    return (
        <>
            {status === OPENING_GHOST_PLACEMENT_ID && (
                <OpeningGhostPlacement {...props}/>
            )}
            {status === CHOOSE_FACTION_ID && (
                <ChooseFaction {...props}/>
            )}
            {/* DraftOverlay is status-driven, not gamePlayerData-gated like
                the two pickers above — it renders for every seated client
                whenever any seat is at PICK_DRAFT_ID, so it self-gates
                internally and mounts unconditionally here. */}
            <DraftOverlay {...props}/>
        </>
    );
}

// The River occupies a full row in TV Observer, Tablet, and Solo
// Desktop presets — 110-180px depending. When every orbit slot is
// empty (game not yet rolling, between burns, or just no guests in
// flight) that row should occupy zero real estate, not sit there as
// a blank band. `riverIsEmpty` returns true for any shape of empty
// — missing gameData, missing orbits, all-falsy orbits.
function riverIsEmpty(gameData) {
    if (!gameData) return true;
    if (!Array.isArray(gameData.orbits)) return true;
    return gameData.orbits.every((cardId) => !cardId);
}

// PresetPicker is a small affordance in the corner of the dashboard
// that lets the user swap presets. Persistence happens through the
// useDashboardPreset hook.
function PresetPicker({ presetName, setPresetName }) {
    return (
        <div className={'dashboard-preset-picker'}>
            <label htmlFor={'dashboard-preset-select'}
                   className={'dashboard-preset-picker__label'}>
                Layout
            </label>
            <select id={'dashboard-preset-select'}
                    className={'dashboard-preset-picker__select'}
                    value={presetName}
                    onChange={(e) => setPresetName(e.target.value)}>
                {PRESET_NAMES.map((name) => (
                    <option key={name} value={name}>
                        {PRESET_DISPLAY_NAMES[name]}
                    </option>
                ))}
            </select>
        </div>
    );
}

function OnDemandSheet({ open, onClose, title, children }) {
    if (!open) return null;
    return (
        <div className={'dashboard-sheet'}
             role={'dialog'}
             aria-label={title}>
            <div className={'dashboard-sheet__backdrop'}
                 onClick={onClose}/>
            <div className={'dashboard-sheet__panel'}>
                <div className={'dashboard-sheet__header'}>
                    <span className={'dashboard-sheet__title'}>
                        {title}
                    </span>
                    <button type={'button'}
                            className={'dashboard-sheet__close'}
                            onClick={onClose}
                            aria-label={'Close ' + title}>
                        &times;
                    </button>
                </div>
                <div className={'dashboard-sheet__body'}>
                    {children}
                </div>
            </div>
        </div>
    );
}

function Panel({ name, preset, className, children }) {
    const cfg = preset.panels[name];
    if (!cfg || !cfg.visible || cfg.mode === 'on-demand') return null;
    if (!cfg.gridArea) return null;
    const classes = [
        'dashboard__panel',
        'dashboard__panel--' + name,
        className,
    ].filter(Boolean).join(' ');
    return (
        <div className={classes}
             data-panel={name}
             style={{ gridArea: cfg.gridArea }}>
            {children}
        </div>
    );
}

// DesktopRender — Desktop preset's bifurcated render path. Top and
// ticker are full-width bands; the middle canvas hosts three
// react-rnd-driven floating windows (advisor, hotel, competitors)
// whose position / size / z-index persist per panel via
// useFloatingWindows. None of the existing <Panel> / grid-template
// machinery runs on this path.
function DesktopRender(props) {
    const seated = !!props.gamePlayerData;
    const canvasRef = useRef(null);
    const {
        windows, updateWindow, bringToFront, resetLayout, dumpLayout,
    } = useFloatingWindows(canvasRef);

    // CC_LOBBY_OVERLAY_WINDOW: the summonable Lobby overlay. Open flag +
    // geometry are local (not in useFloatingWindows — that hook always
    // renders its panels and persists them; this window toggles). Mounting
    // LobbyOverlay only while open means closing it unmounts the component,
    // which removes its `group-update-event` listener — no zombie listeners,
    // and a reopen re-seeds fresh from the broadcast cache. Closing is a
    // pure view dismissal (StrictMode-safe: no persistent side effect).
    const [lobbyWindowOpen, setLobbyWindowOpen] = useState(false);
    const [lobbyWindowState, setLobbyWindowState] =
        useState(LOBBY_WINDOW_DEFAULT);
    useEffect(() => {
        const toggle = () => setLobbyWindowOpen((open) => !open);
        window.addEventListener(TOGGLE_LOBBY_WINDOW_EVENT, toggle);
        return () => window.removeEventListener(
            TOGGLE_LOBBY_WINDOW_EVENT, toggle);
    }, []);
    const updateLobbyWindow = useCallback((name, partial) => {
        setLobbyWindowState((prev) => ({ ...prev, ...partial }));
    }, []);
    // Single summonable window already floats above the persistent panels
    // via its high default z; focus just keeps it pinned there.
    const focusLobbyWindow = useCallback(() => {
        setLobbyWindowState((prev) => ({
            ...prev, z: LOBBY_WINDOW_DEFAULT.z,
        }));
    }, []);
    const closeLobbyWindow = useCallback(
        () => setLobbyWindowOpen(false), []);

    return (
        <div className={'dashboard dashboard--desktop'}
             data-testid={'dashboard'}>
            <PresetPicker presetName={DESKTOP}
                          setPresetName={props.setPresetName}/>
            <div className={'dashboard-desktop-toolbar'}>
                <button type={'button'}
                        className={'dashboard-toolbar-btn'}
                        onClick={resetLayout}
                        data-testid={'reset-layout'}>
                    Reset Layout
                </button>
                <button type={'button'}
                        className={'dashboard-toolbar-btn'}
                        onClick={dumpLayout}
                        data-testid={'dump-layout'}>
                    Dump Layout
                </button>
            </div>

            <div className={'dashboard__panel '
                + 'dashboard__panel--topBar'}>
                {/*<Game2dMenu {...props}/>*/}
                {seated && <GamePlayer2dMenu {...props}/>}
            </div>

            <div className={'dashboard__canvas'}
                 ref={canvasRef}
                 data-testid={'desktop-canvas'}>
                <FloatingWindow name={'aiAdvisor'}
                                title={'AI Advisor'}
                                state={windows.aiAdvisor}
                                onChange={updateWindow}
                                onFocus={bringToFront}>
                    {seated && props.app && props.app.game
                        && props.app.game.seatedController
                        && <AutomaticPlayerControls {...props}/>}
                </FloatingWindow>

                <FloatingWindow name={'yourHotel'}
                                title={'Your Hotel'}
                                state={windows.yourHotel}
                                onChange={updateWindow}
                                onFocus={bringToFront}>
                    {seated && <HotelInterface {...props}/>}
                    {seated && <BoardActionRouter {...props}/>}
                </FloatingWindow>

                <FloatingWindow name={'competitors'}
                                title={'Competitors'}
                                state={windows.competitors}
                                onChange={updateWindow}
                                onFocus={bringToFront}>
                    <CompetitorsWindow {...props}/>
                </FloatingWindow>

                <FloatingWindow name={'action'}
                                title={'Actions'}
                                state={windows.action}
                                onChange={updateWindow}
                                onFocus={bringToFront}>
                    <ActionWindow {...props}/>
                </FloatingWindow>

                <FloatingWindow name={'lodging'}
                                title={'Lodging'}
                                state={windows.lodging}
                                onChange={updateWindow}
                                onFocus={bringToFront}>
                    <LodgingActionWindow {...props}/>
                </FloatingWindow>

                <FloatingWindow name={'research'}
                                title={'Research'}
                                state={windows.research}
                                onChange={updateWindow}
                                onFocus={bringToFront}>
                    <ResearchActionWindow {...props}/>
                </FloatingWindow>

                <FloatingWindow name={'ghost'}
                                title={'Ghost'}
                                state={windows.ghost}
                                onChange={updateWindow}
                                onFocus={bringToFront}>
                    <GhostActionWindow {...props}/>
                </FloatingWindow>

                <FloatingWindow name={'orbitalRiver'}
                                title={'Orbital River'}
                                state={windows.orbitalRiver}
                                onChange={updateWindow}
                                onFocus={bringToFront}>
                    {props.gameData
                        && <TheRiver {...props} bind={true}/>}
                </FloatingWindow>

                <FloatingWindow name={'activity'}
                                title={'Activity'}
                                state={windows.activity}
                                onChange={updateWindow}
                                onFocus={bringToFront}>
                    <ActivityFeed {...props}/>
                </FloatingWindow>

                <FloatingWindow name={'insights'}
                                title={'Insights'}
                                state={windows.insights}
                                onChange={updateWindow}
                                onFocus={bringToFront}>
                    <InsightsWindow/>
                </FloatingWindow>

                <FloatingWindow name={'scores'}
                                title={'Scores'}
                                state={windows.scores}
                                onChange={updateWindow}
                                onFocus={bringToFront}>
                    <ScoresWindow {...props}/>
                </FloatingWindow>

                {/* CC_LOBBY_OVERLAY_WINDOW: the summonable Lobby overlay.
                    Rendered ONLY while open so a close unmounts LobbyOverlay
                    and tears down its broadcast listener. */}
                {lobbyWindowOpen &&
                    <FloatingWindow name={LOBBY_WINDOW_NAME}
                                    title={LOBBY_WINDOW_TITLE}
                                    state={lobbyWindowState}
                                    onChange={updateLobbyWindow}
                                    onFocus={focusLobbyWindow}>
                        <LobbyOverlay app={props.app}
                                      onClose={closeLobbyWindow}/>
                    </FloatingWindow>
                }
            </div>

            <EventRouter {...props}/>
            <CeremonyOverlays {...props}/>

            {/* DeckGrid self-gates on its internal `show` state, which
                toggles via the `toggle-deck-grid-event` window event
                fired by DeckBar's progress-bar click. Mounting it here
                gives the Desktop preset a listener — without this,
                clicking the progressbar in GamePlayer2dMenu fires the
                event into the void. Desktop-specific modal styling
                lives in deck-bar.css under .dashboard--desktop. */}
            <DeckGrid {...props}/>
        </div>
    );
}

export function Dashboard(props) {
    const [presetName, setPresetName] = useDashboardPreset();
    // Hooks must be called unconditionally every render. The
    // Desktop branch below doesn't use pools / deck, but the
    // hooks must run anyway so the hook order stays stable
    // when presetName flips between Desktop and other presets.
    const pools = useOnDemandPanel('poolsPanel');
    const deck = useOnDemandPanel('deckInspector');

    // Hoisted above the preset branch: AdvertisementPossibilities (the
    // AC) is a singleton mounted once per seated Dashboard, not a
    // per-router conditional mount. It owns the ADVERTISING_ID +
    // WAIT_ID substate and gates its own dispatches internally, so it
    // sits as a sibling of the layout switch and persists across preset
    // changes. Gated on `seated` because its constructor reaches
    // app.game.seatedController.gamePlayerApi, which is null in observer
    // mode. See AdvertisementController.isActive / componentDidUpdate.
    const seated = !!props.gamePlayerData;
    const advertisementSingleton =
        seated && <AdvertisementPossibilities {...props}/>;

    if (presetName === DESKTOP) {
        return (
            <>
                {advertisementSingleton}
                <DesktopRender {...props}
                               setPresetName={setPresetName}/>
            </>
        );
    }

    const basePreset = PRESETS[presetName] || PRESETS[PRESET_NAMES[0]];
    // Empty river collapses out of the grid entirely — no reserved
    // track, no blank band. TheRiver itself also returns null in this
    // case, which covers the nested copies inside GamePlayer2d /
    // Game2d that aren't governed by the dashboard's grid.
    const preset = riverIsEmpty(props.gameData)
        ? collapseEmptyAreaFromPreset(basePreset, 'river')
        : basePreset;

    const rootClasses = [
        'dashboard',
        'dashboard--' + presetName,
        preset.externalAdvisor ? 'dashboard--external-advisor' : '',
        preset.externalRiver ? 'dashboard--external-river' : '',
    ].filter(Boolean).join(' ');

    const poolsOnDemand =
        preset.panels.poolsPanel
        && preset.panels.poolsPanel.mode === 'on-demand'
        && preset.panels.poolsPanel.visible;
    const deckOnDemand =
        preset.panels.deckInspector
        && preset.panels.deckInspector.mode === 'on-demand'
        && preset.panels.deckInspector.visible;

    return (
        <>
        {advertisementSingleton}
        <div className={rootClasses}
             data-testid={'dashboard'}
             style={{
                 gridTemplateAreas: preset.gridTemplateAreas,
                 gridTemplateColumns: preset.gridTemplateColumns,
                 gridTemplateRows: preset.gridTemplateRows,
             }}>
            <PresetPicker presetName={presetName}
                          setPresetName={setPresetName}/>

            <Panel name={'topBar'} preset={preset}>
                <Game2dMenu {...props}/>
                {seated && <GamePlayer2dMenu {...props}/>}
            </Panel>

            <Panel name={'competitors'} preset={preset}>
                <Game2d {...props}/>
            </Panel>

            <Panel name={'yourHotel'} preset={preset}>
                {seated && <GamePlayer2d {...props}/>}
            </Panel>

            <Panel name={'orbitalRiver'} preset={preset}>
                {props.gameData && <TheRiver {...props} bind={false}/>}
            </Panel>

            <Panel name={'aiAdvisor'} preset={preset}>
                {seated && props.app && props.app.game
                    && props.app.game.seatedController
                    && <AutomaticPlayerControls {...props}/>}
            </Panel>

            <Panel name={'poolsPanel'} preset={preset}>
                <GamePlayerPools2d {...props}/>
            </Panel>

            <Panel name={'deckInspector'} preset={preset}>
                <DeckGrid {...props}/>
            </Panel>

            <Panel name={'ticker'} preset={preset}>
                <TranscriptTicker {...props}/>
                <MessageTicker {...props}/>
            </Panel>

            <EventRouter {...props}/>
            <CeremonyOverlays {...props}/>

            {poolsOnDemand && (
                <button type={'button'}
                        className={'dashboard-on-demand-button '
                            + 'dashboard-on-demand-button--pools'}
                        onClick={pools.toggle}
                        data-testid={'pools-button'}>
                    Pools
                </button>
            )}
            {deckOnDemand && (
                <button type={'button'}
                        className={'dashboard-on-demand-button '
                            + 'dashboard-on-demand-button--deck'}
                        onClick={deck.toggle}
                        data-testid={'deck-button'}>
                    Deck
                </button>
            )}

            <OnDemandSheet open={pools.isOpen}
                           onClose={pools.close}
                           title={'Pools'}>
                <GamePlayerPools2d {...props}/>
            </OnDemandSheet>
            <OnDemandSheet open={deck.isOpen}
                           onClose={deck.close}
                           title={'Deck Inspector'}>
                <DeckGrid {...props}/>
            </OnDemandSheet>
        </div>
        </>
    );
}

export default Dashboard;
